import React from "react";
import IcomoonIcon from "@icons/IcomoonIcon";
import { LanguageKey } from "@/types/generated/LanguageKey";
import { translate } from "@/hooks/useLanguage";

/**
 * The fixed vocabulary of reasons a page can be unavailable in the current
 * context (STIC-15790). Every page that draws the blank picks one of these —
 * never free text — so the wording stays consistent across the many pages
 * sharing the pattern. New reasons get a new constant plus their own i18n
 * hint key in the map below.
 */
export const NotAvailableReasonConst = {
  /** The page needs one organisation and "All" has none sliced. */
  NOT_AVAILABLE_ON_ALL: "not_available_on_all",
  /** The page's objects only exist in NORMAL organisations. */
  ONLY_ON_NORMAL: "only_on_normal",
  /** The page describes a partner, so only a PARTNER organisation has one. */
  ONLY_ON_PARTNER: "only_on_partner",
  /** Partner Quotes: the selected partner is not a commercial channel partner on the EU price list. */
  PARTNER_NOT_COMMERCIAL_EU: "partner_not_commercial_eu",
  /** The page's objects only exist on the System organisation or a PARTNER organisation. */
  ONLY_ON_SYSTEM_OR_PARTNER: "only_on_system_or_partner",
  /** STIC-11419: a STOCK organisation only holds devices and asset tags. */
  NOT_ON_STOCK: "not_on_stock",
  /** The page does not apply to the sliced PARTNER organisation. */
  NOT_AT_THIS_PARTNER: "not_at_this_partner",
} as const;

export type NotAvailableReason =
  (typeof NotAvailableReasonConst)[keyof typeof NotAvailableReasonConst];

const REASON_HINT_KEYS: Record<NotAvailableReason, LanguageKey> = {
  [NotAvailableReasonConst.NOT_AVAILABLE_ON_ALL]:
    LanguageKey.not_available_at_this_level_hint_key,
  [NotAvailableReasonConst.ONLY_ON_NORMAL]:
    LanguageKey.not_available_requires_normal_org_hint_key,
  [NotAvailableReasonConst.ONLY_ON_PARTNER]:
    LanguageKey.not_available_requires_partner_org_hint_key,
  [NotAvailableReasonConst.PARTNER_NOT_COMMERCIAL_EU]:
    LanguageKey.not_available_partner_not_commercial_eu_hint_key,
  [NotAvailableReasonConst.ONLY_ON_SYSTEM_OR_PARTNER]:
    LanguageKey.not_available_requires_system_or_partner_org_hint_key,
  [NotAvailableReasonConst.NOT_ON_STOCK]:
    LanguageKey.not_available_on_stock_org_hint_key,
  [NotAvailableReasonConst.NOT_AT_THIS_PARTNER]:
    LanguageKey.not_available_at_this_partner_hint_key,
};

interface NotAvailableAtThisLevelProps {
  /**
   * Why this context doesn't work — defaults to the "not available on All"
   * wording, which points the user at the org pill selector.
   */
  reason?: NotAvailableReason;
}

/**
 * Shared content-panel replacement for level-unavailable menu items (wiki
 * Rule #5, STIC-15615 R9/R11) — covers the greyed menu items, direct
 * navigation and deep links. The sidebar stays where it is; only the content
 * area explains why the page has nothing to show here.
 */
const NotAvailableAtThisLevel: React.FC<NotAvailableAtThisLevelProps> = ({
  reason = NotAvailableReasonConst.NOT_AVAILABLE_ON_ALL,
}) => {
  return (
    <div className="d-flex flex-column align-items-center justify-content-center text-center py-5">
      <div className="mb-2" style={{ opacity: 0.4 }}>
        <IcomoonIcon icon="blocked" size={30} />
      </div>
      <h4>{translate(LanguageKey.not_available_at_this_level_key)}</h4>
      <p className="text-muted" style={{ maxWidth: 360 }}>
        {translate(REASON_HINT_KEYS[reason])}
      </p>
    </div>
  );
};

export default NotAvailableAtThisLevel;
